import { environment } from '../../environments/environment';
import { AlertifyService } from './alertify.service';
import { Injectable, signal } from '@angular/core';
import { JwtHelperService } from '@auth0/angular-jwt';
import { Router } from '@angular/router';
import { Subject, lastValueFrom } from 'rxjs';
import { AuthService, User } from '@auth0/auth0-angular';
import { jwtDecode } from 'jwt-decode';
import { UserDto } from '../_dtos/users/user-dto';
import { OrganizationDto } from '../_dtos/organizations/organization-dto';
import { Permissions } from '../_enums/permissions';
import { HttpClient } from '@angular/common/http';
import { UserRegistrationDto } from '../_dtos/auth/UserRegistrationDto';


@Injectable({
    providedIn: 'root',
})
export class AppAuthService {
    private _baseUrl = environment.apiUrl + 'auth/';
    private _jwtHelper = new JwtHelperService();
    private _currentOrgStorageName = 'lido-bo-current-org';
    private _userPermissionsStorageName = 'lido-bo-user-permissions';
    private _respectsSuperUser = environment.respectSuper;

    isLoggedIn = signal(false);
    currentOrganization: OrganizationDto;
    currentOrganizationSub = new Subject<OrganizationDto>();
    user = signal<UserDto | null>(null);

    constructor(
        private afy: AlertifyService,
        private router: Router,
        private _auth: AuthService,
        private _http: HttpClient
    ) {
        const currentOrganization = this._getCurrentOrganization();
        if (currentOrganization != null) {
            this.currentOrganization = currentOrganization;
            this.currentOrganizationSub.next(this.currentOrganization);
        }
        else {
            this.currentOrganizationSub.next(null);
        }
    }

    signup(dto: UserRegistrationDto) {
        return lastValueFrom(this._http.post<User>(this._baseUrl + 'register', dto));
    }

    getUserCurrentOrganization() {
        return lastValueFrom(this._http.get<OrganizationDto>(this._baseUrl + 'current-organization'));
    }

    async setCurrentOrganization(organization: OrganizationDto) {
        if (!this.isLoggedIn) return;
        if (this.user == null) return;

        this.currentOrganization = organization;

        const userCurrentOrg = await this.getUserCurrentOrganization();

        this.currentOrganization = userCurrentOrg;

        localStorage.setItem(this._currentOrgStorageName, JSON.stringify(userCurrentOrg));

        this.afy.success('organization changed successfully');
        this.router.navigate(['/']);
    }

    getCurrentOrganizationId() {
        const currentOrg = this._getCurrentOrganization();
        if (!currentOrg) return null;

        return currentOrg.id;
    }

    hasPermissions(permission: string[], requiredAll: boolean = true): boolean {
        if (this._respectsSuperUser && this.isSuperUser()) return true;

        const userPermissions = this.getUserPermissions();

        if (requiredAll) {
            return permission.every(per => this._checkPermission(userPermissions, per));
        } else {
            return permission.some(per => this._checkPermission(userPermissions, per));
        }
    }

    getUserPermissions(): string[] {
        try {
            const userPermissions = JSON.parse(localStorage.getItem(this._userPermissionsStorageName));
            return userPermissions;
        }
        catch (error) {
            return [];
        }
    }

    isSuperUser() {
        const userPermissions = this.getUserPermissions();

        if (!userPermissions || !userPermissions.length) return false;

        return userPermissions.includes(Permissions.SuperUser);
    }

    getUserOrganizations(): OrganizationDto[] {
        try {
            const userPermissions = this.getUserPermissions();
            const orgs = Array.from(new Set(userPermissions.map(x => x.split(':')[0])));
            return orgs.map(x => ({ name: x }));
        }
        catch (error) {
            return [];
        }
    }

    async _initialize() {
        const token = await this._getToken();
        const isTokenValid = this._isTokenValid(token);

        await this._updateUserAuth0Id(token);

        if (token && isTokenValid) {
            this.user.set(jwtDecode<UserDto>(token));

            const userPermissions = this.user().permissions;
            localStorage.setItem(this._userPermissionsStorageName, JSON.stringify(userPermissions));

            this.isLoggedIn.set(true);
        }
    }

    destroy() {
        localStorage.removeItem(this._currentOrgStorageName);
        localStorage.removeItem(this._userPermissionsStorageName);
        this.user.set(null);
        this.currentOrganization = null;
        this.currentOrganizationSub.next(this.currentOrganization);
        this.isLoggedIn.set(false);
        this.router.navigate(['/']);
    }

    private _updateUserAuth0Id(token: string) {
        return lastValueFrom(this._http.post(this._baseUrl + 'update-user-auth0id', { token }));
    }

    private _checkPermission(userPermissions: string[], permission: string) {
        return userPermissions.some(x => {
            const [org, per] = x.split(':');
            return org.toLowerCase() === this.currentOrganization.name.toLowerCase() && per.toLowerCase() === permission.toLowerCase();
        });
    }

    private _getCurrentOrganization(): OrganizationDto {
        const storedOrganization = localStorage.getItem(this._currentOrgStorageName);

        if (storedOrganization) {
            const org: OrganizationDto = JSON.parse(storedOrganization);
            return org;
        }

        return null;
    }

    private _getToken() {
        try {
            return lastValueFrom(this._auth.getAccessTokenSilently());
        }
        catch (error) {
            this.afy.error('Error retrieving token');
            return null;
        }
    }

    private _isTokenValid(token: string | null) {
        if (!token) return false;

        return !this._jwtHelper.isTokenExpired(token);
    }
}
